import { apiRoot } from "services/src/config";
import { applySorting } from "utils";
import { fetchListResult } from "components/list/utils";
import { queryClient } from "utils/react-query";
import { useQuery } from "@tanstack/react-query";

/**
 * Custom hook for fetching list data with TanStack Query
 * @param {Object} params - Query parameters
 * @param {string} params.endpoint - API endpoint
 * @param {number} params.page - Current page number
 * @param {number} params.perPage - Items per page
 * @param {string} params.search - Search query
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortOrder - Sort order (asc/desc)
 * @param {Object} params.filters - Filter object
 * @param {Object} params.meta - Additional metadata
 * @param {boolean} params.enabled - Whether the query should run
 * @param {Object} queryOptions - Additional TanStack Query options
 */

export const useListQuery = (params, queryOptions = {}) => {
  const {
    endpoint,
    page = 1,
    perPage = 25,
    search = "",
    sortBy,
    sortOrder = "desc",
    filters = {},
    meta = {},
    enabled = true,
    attrs,
    custom = false,
  } = params;

  const queryKey = [
    endpoint,
    "list",
    {
      page,
      perPage,
      search,
      sortBy,
      sortOrder,
      filters,
      fields: meta.fields,
      filter: meta.filter,
      custom: Boolean(custom),
    },
  ];

  const queryFn = async () => {
    const query = {
      limit: perPage,
      page: page,
      sort: applySorting(sortBy, sortOrder),
      fields: meta.fields,
    };

    // Only include deep when it has actual content
    if (meta.deep && Object.keys(meta.deep).length > 0) {
      query.deep = meta.deep;
    }
    // Only include search when non-empty
    if (search && search.trim() !== "") {
      query.search = search;
    }

    // Add filters to the query
    if (
      (filters && Object.keys(filters).length > 0) ||
      meta.filter ||
      (search && meta.customSearch)
    ) {
      // Deep clone the filters to avoid modifying the original
      const processedFilters = JSON.parse(JSON.stringify(filters));

      // Process each filter to remove properties starting with "_"
      Object.keys(processedFilters).forEach((key) => {
        if (
          processedFilters[key] &&
          typeof processedFilters[key] === "object"
        ) {
          // Remove any properties that start with "_"

          if (key.startsWith("_") && key !== "_or" && key !== "_and") {
            delete processedFilters[key];
            return;
          }
          Object.keys(processedFilters[key]).forEach((prop) => {
            if (prop.startsWith("__")) {
              delete processedFilters[key][prop];
            }
          });
        }

        // If the filter is now empty, remove it entirely
        if (
          processedFilters[key] &&
          Object.keys(processedFilters[key]).length === 0
        ) {
          delete processedFilters[key];
        }
      });
      let filterQuery = { ...meta.filter, ...processedFilters };
      if (search && meta.customSearch) {
        const searchFilter = meta.customSearch(search);
        filterQuery = Object.keys(filterQuery).length > 0 ? { _and: [filterQuery, searchFilter] } : searchFilter;
        delete query.search;
      }
      query.filter = filterQuery;
    }

    try {
      const { items, count, meta: resMeta } = await fetchListResult(endpoint, query, { custom });
      return { items, count, meta: resMeta };
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: enabled && !!endpoint,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true, // Useful for pagination
    ...queryOptions,
  });
};

export const createListQuery = (params, queryOptions = {}) => {
  const {
    endpoint,
    page = 1,
    perPage = 25,
    search = "",
    sortBy,
    sortOrder = "desc",
    filters = {},
    meta = {},
    enabled = true,
    attrs,
    custom = false,
  } = params;

  const queryKey = [
    endpoint,
    "list",
    {
      page,
      perPage,
      search,
      sortBy,
      sortOrder,
      filters,
      fields: attrs || meta.fields,
      filter: meta.filter,
      groupBy: meta?.groupBy,
      custom: Boolean(custom),
    },
  ];

  const processedFilters = JSON.parse(JSON.stringify(filters));
  let deepConfig = meta.deep ? { ...meta.deep } : {};

  // Extract and merge deep filters
  if (processedFilters.deep) {
    deepConfig = mergeDeepConfigs(deepConfig, processedFilters.deep);
    // Remove deep from regular filters
    delete processedFilters.deep;
  }

  const queryFn = async () => {
    const query = {
      limit: perPage,
      page: page,
      sort: applySorting(sortBy, sortOrder),
      fields: attrs || meta.fields,
      ...(meta.groupBy ? { groupBy: meta.groupBy } : {}),
    };

    // Only include deep when it has actual content
    if (deepConfig && Object.keys(deepConfig).length > 0) {
      query.deep = deepConfig;
    }
    // Only include search when non-empty
    if (search && search.trim() !== "") {
      query.search = search;
    }

    // Add filters to the query
    if (
      (processedFilters && Object.keys(processedFilters).length > 0) ||
      meta.filter ||
      (search && meta.customSearch)
    ) {
      // Process each filter to remove properties starting with "_"
      Object.keys(processedFilters).forEach((key) => {
        if (
          processedFilters[key] &&
          (typeof processedFilters[key] === "object" ||
            typeof processedFilters[key] === "string")
        ) {
          // Remove any properties that start with "_"
          if (key.startsWith("_") && key !== "_or" && key !== "_and") {
            delete processedFilters[key];
            return;
          }
          Object.keys(processedFilters[key]).forEach((prop) => {
            if (prop.startsWith("__")) {
              delete processedFilters[key][prop];
            }
          });
        }

        // If the filter is now empty, remove it entirely
        if (
          processedFilters[key] &&
          Object.keys(processedFilters[key]).length === 0
        ) {
          delete processedFilters[key];
        }
      });
      let filterQuery = { ...meta.filter, ...processedFilters };
      if (search && meta.customSearch) {
        const searchFilter = meta.customSearch(search);
        filterQuery = Object.keys(filterQuery).length > 0 ? { _and: [filterQuery, searchFilter] } : searchFilter;
        delete query.search;
      }
      query.filter = filterQuery;
    }

    try {
      return await fetchListResult(endpoint, query, {
        flowId: meta?.flowId,
        custom,
        itemKey: meta?.itemKey,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  return {
    queryKey,
    queryFn,
    enabled: enabled && !!endpoint,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    placeholderData: (previousData) => previousData, // Replaces keepPreviousData
    ...queryOptions,
  };
};

// Helper function to merge deep configurations
const mergeDeepConfigs = (baseDeep, filterDeep) => {
  const merged = { ...baseDeep, ...filterDeep };

  const mergeRecursive = (target, source) => {
    Object.keys(source).forEach((key) => {
      if (key === "_filters") {
        // Special handling for _filters - merge with existing _filter
        if (!target._filter) target._filter = {};
        target._filter = { ...target._filter, ...source._filters };
      } else if (
        typeof source[key] === "object" &&
        source[key] !== null &&
        !Array.isArray(source[key])
      ) {
        // Recursive merge for nested objects
        if (!target[key]) target[key] = {};
        mergeRecursive(target[key], source[key]);
      } else {
        // Direct assignment for primitive values
        target[key] = source[key];
      }
    });
  };

  mergeRecursive(merged, filterDeep);
  return merged;
};

export const fetchListData = async (params, queryOptions = {}) => {
  const queryConfig = createListQuery(params, queryOptions);
  return await queryClient.fetchQuery(queryConfig);
};

export const customListQuery = (params, queryOptions = {}) => {
  const {
    endpoint,
    filters = {},
    enabled = true,
    perPage,
    page,
    search,
    sortBy,
    sortOrder = "desc",
  } = params;

  const queryKey = [
    endpoint,
    "list",
    {
      page,
      limit: perPage,
      filters,
      search,
      sortBy,
      sortOrder,
    },
  ];

  const buildCustomSort = (sortBy, sortOrder = "desc") => {
    if (!sortBy) {
      return "";
    }

    return `${sortOrder === "desc" ? "-" : ""}${sortBy}`;
  };

  const queryFn = async () => {
    let query;
    if (endpoint?.startsWith("custom-query-panel")) {
      const {
        query_id,
        sortBy: filterSortBy,
        sortOrder: filterSortOrder,
        ...rest
      } = filters || {};
      const sort = buildCustomSort(
        sortBy || filterSortBy,
        sortOrder || filterSortOrder,
      );
      const variables = Object.entries(rest || {})
        .filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        )
        .map(([key, value]) => ({ key, value }));

      query = {
        params: {
          ...(query_id ? { query_id } : {}),
          ...(variables.length ? { variables } : {}),
          ...(search ? { search } : {}),
          ...(perPage ? { limit: perPage } : {}),
          ...(page ? { page } : {}),
          ...(sort ? { sort } : {}),
        },
      };
    } else {
      const sort = buildCustomSort(sortBy, sortOrder);
      query = {
        params: {
          ...filters,
          limit: perPage,
          page,
          search,
          ...(sort ? { sort } : {}),
        },
      };
    }
    try {
      // Choose client: legacy endpoints go via /custom-ui/, new ones via root

      const response = await apiRoot.get(endpoint, query);

      return {
        items: response.data.data,
        count: response.data?.totalRecords ?? 0,
        meta: { totals: response.data?.totals },
      };
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  return {
    queryKey,
    queryFn,
    enabled: enabled && !!endpoint,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    placeholderData: (previousData) => previousData, // Replaces keepPreviousData
    ...queryOptions,
  };
};

export const fetchCustomListData = async (params, queryOptions = {}) => {
  const queryConfig = customListQuery(params, queryOptions);
  return await queryClient.fetchQuery(queryConfig);
};
